import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardHeader, MessageBar, MessageBarBody, MessageBarTitle, Text, makeStyles, tokens } from "@fluentui/react-components";
import { useAuth } from "../lib/auth.tsx";
import { api, type AvailabilitySlot, type Client } from "../lib/api.ts";
import { WEEKDAY_LABEL } from "../lib/format.ts";

const useStyles = makeStyles({
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px", marginTop: "16px" },
  card: { padding: "16px" },
  big: { fontSize: tokens.fontSizeHero800, fontWeight: tokens.fontWeightSemibold },
});

export function DashboardPage() {
  const s = useStyles();
  const { user } = useAuth();
  const t = user?.trainer;
  const clients = useQuery({ queryKey: ["clients"], queryFn: () => api.get<Client[]>("/api/clients") });
  const avail = useQuery({ queryKey: ["availability"], queryFn: () => api.get<{ slots: AvailabilitySlot[] }>("/api/trainers/me/availability").then((r) => r.slots) });
  const inviteUrl = t ? `${window.location.origin}/join/${t.inviteToken}` : "";
  const justSetUp = !!(useLocation().state as { justSetUp?: boolean } | null)?.justSetUp;

  return (
    <div>
      <Text as="h1" size={800} weight="semibold" block>
        Hi {user?.name.split(" ")[0]}
      </Text>
      <Text block style={{ color: tokens.colorNeutralForeground3 }}>
        {t?.gyms.find((g) => g.isPrimary)?.name ?? "No gym set"} · {t?.sessionLengthMinutes} min sessions · check-ins every{" "}
        {t ? WEEKDAY_LABEL[t.availabilityRequestDay] : "—"}
      </Text>

      {justSetUp && (
        <MessageBar intent="success" style={{ marginTop: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>Your week is set up.</MessageBarTitle>
            Next: share your client sign-up link below. Hours change? Tell RackUp on the Availability page any time.
          </MessageBarBody>
        </MessageBar>
      )}
      <div className={s.grid}>
        <Card className={s.card}>
          <CardHeader header={<Text weight="semibold">Clients</Text>} />
          <Text className={s.big}>{clients.data?.length ?? "–"}</Text>
          <Button as="a" href="/clients" appearance="secondary">
            Manage clients
          </Button>
        </Card>
        <Card className={s.card}>
          <CardHeader header={<Text weight="semibold">Availability slots</Text>} />
          <Text className={s.big}>{avail.data?.length ?? "–"}</Text>
          <Button as="a" href="/availability" appearance="secondary">
            Edit availability
          </Button>
        </Card>
        <Card className={s.card}>
          <CardHeader header={<Text weight="semibold">Client sign-up link</Text>} />
          <Text size={200} style={{ wordBreak: "break-all" }}>
            {inviteUrl}
          </Text>
          <Button appearance="primary" onClick={() => void navigator.clipboard.writeText(inviteUrl)}>
            Copy link
          </Button>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Share on WhatsApp — clients add themselves. Rotate it in <Link to="/settings">Settings</Link>.
          </Text>
        </Card>
      </div>
    </div>
  );
}
